import React, { useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "../../components/AdminShell";
import { Badge, Button, Card } from "../../components/ui-kit";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Moon, Sun, UserCircle, UsersThree, CarProfile, FileText, Megaphone, SignOut } from "@phosphor-icons/react";

export default function AdminProfile() {
  const { user, logout, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const [photoUploading, setPhotoUploading] = useState(false);
  const links = [
    { to: "/admin/users", label: "Utilisateurs", icon: UsersThree },
    { to: "/admin/drivers", label: "Chauffeurs", icon: CarProfile },
    { to: "/admin/documents", label: "Documents", icon: FileText },
    { to: "/admin/ads", label: "Publicites", icon: Megaphone },
  ];
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo admin mise a jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  return (
    <AdminShell title="Profil admin">
      <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <label className="relative flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-2xl bg-primary/15 text-primary">
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={user?.name || "Admin"} className="h-full w-full rounded-2xl object-cover" />
              ) : (
                <UserCircle size={34} weight="duotone" />
              )}
              <span className="absolute bottom-1 right-1 rounded-full bg-black/60 p-1 text-white"><Camera size={13} weight="bold" /></span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={photoUploading} />
            </label>
            <div className="min-w-0">
              <h1 className="truncate font-heading text-2xl font-black">{user?.name || "Admin Nexora"}</h1>
              <div className="truncate font-mono text-xs text-muted-foreground">{user?.email}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge tone="primary">admin</Badge>
                <Badge tone={user?.email_verified ? "success" : "warning"}>{user?.email_verified ? "email verifie" : "email non verifie"}</Badge>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-3 rounded-2xl bg-white/5 p-4 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Telephone</span>
              <span className="font-mono">{user?.phone || "-"}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Session</span>
              <span className="font-bold">active</span>
            </div>
          </div>

          <Button variant="outline" className="mt-5 w-full" onClick={logout}>
            <SignOut weight="bold" /> Deconnexion
          </Button>
        </Card>

        <div className="space-y-5">
          <Card className="p-5">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Affichage</div>
            <div className="grid grid-cols-2 overflow-hidden rounded-full border border-white/10 bg-white/5 p-1">
              {[
                { key: "dark", label: "Sombre", icon: Moon },
                { key: "light", label: "Clair", icon: Sun },
              ].map((item) => {
                const Icon = item.icon;
                const active = theme === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTheme(item.key)}
                    className={`flex items-center justify-center gap-2 rounded-full px-3 py-3 text-xs font-bold uppercase tracking-widest ${
                      active ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <Icon size={16} weight="bold" /> {item.label}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Acces rapide</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {links.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} className="flex items-center gap-3 rounded-2xl bg-white/5 p-4 hover:bg-white/10">
                    <div className="rounded-xl bg-primary/15 p-2 text-primary"><Icon size={20} weight="duotone" /></div>
                    <div className="text-sm font-bold">{item.label}</div>
                  </Link>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
